import { gpHome } from '../app';
import { ISimpleHttpService } from '../../app-common/services/authorized-http-service';
import {
    IStorageProviderFactory,
    StorageProviderFactoryServiceName,
} from '../../app-common/services/storage-provider-contract';
import { gpGenerateUniqueId } from '../../app-common/util/generate-ids';
import { CREDITS_EXHAUSTED_MESSAGE, isCreditsExhausted } from '../../app-common/util/ai-credit-errors';
import {
    AnalysisProject,
    AnalysisProjectProcessingServiceName,
    AnalysisProjectServiceName,
    IAnalysisProjectProcessingService,
    IAnalysisProjectService,
    Report,
} from './analysis-contract';

const API_BASE = '/api/gameplan/analysis-projects';

class AnalysisProjectService implements IAnalysisProjectService {

    static $inject = ['$q', '$httpAuth', StorageProviderFactoryServiceName, AnalysisProjectProcessingServiceName];
    static serviceName = AnalysisProjectServiceName;

    constructor(
        private readonly $q: ng.IQService,
        private readonly $http: ISimpleHttpService,
        private readonly storageFactory: IStorageProviderFactory,
        private readonly processingService: IAnalysisProjectProcessingService,
    ) { }

    getProjects(): ng.IPromise<AnalysisProject[]> {
        return this.$http.get(API_BASE).then((response: any) => {
            const data = response.data || response;
            const projects: AnalysisProject[] = (data as any[]).map(p => this.mapServerProject(p));
            return projects.map(p => this.enrichProject(p));
        });
    }

    getProject(id: string): ng.IPromise<AnalysisProject> {
        const sp = this.storageFactory.getProvider('analysis-projects/' + id);

        return sp.read('metadata.json').then(buffer => {
            const json = new TextDecoder().decode(buffer);
            const data = JSON.parse(json);
            const project = this.mapServerProject(data);
            return this.enrichProject(project);
        }).then((project: AnalysisProject) => {
            if (project.reports.length > 0) return this.$q.resolve(project);
            return this.loadSavedReports(project);
        });
    }

    /**
     * Upload a new project file. The client drives everything:
     * 1. Generate a ULID for the project ID
     * 2. Write project.json metadata via storage provider
     * 3. Upload the source file via storage provider
     * 4. POST /convert to start the convert + analyze pipeline
     *    (previously triggered by the deleted auto-convert-project blob
     *    trigger; see RA0038)
     * 5. Return the project so the UI can navigate to it (which then
     *    polls metadata.json for the convert orchestrator to flip
     *    status to "imported" / "error")
     */
    createProject(file: File): ng.IPromise<AnalysisProject> {
        const projectId = gpGenerateUniqueId();
        const ext = this.extractExtension(file.name);
        const internalFileName = '00_project' + ext;
        const sp = this.storageFactory.getProvider('analysis-projects/' + projectId);

        const metadata = {
            id: projectId,
            originalFileName: file.name,
            internalFileName,
            uploadedUtc: new Date().toISOString(),
            projectContext: '',
        };

        // Write metadata, then upload the file
        const metaBlob = new Blob(
            [JSON.stringify(metadata, null, 2)],
            { type: 'application/json' },
        );

        return sp.write('metadata.json', metaBlob, 'application/json').then(() => {
            return sp.write(internalFileName, file, file.type || 'application/octet-stream');
        }).then(() => {
            // Explicitly start the pipeline orchestrator. /convert returns
            // 202 immediately; analyze-detail then polls metadata.json for
            // status to flip to "imported" / "error". Awaited (not
            // fire-and-forget) so a 5xx surfaces as an error from
            // createProject — same failure surface the user has today if
            // any of the blob writes fail.
            return this.$http.post(API_BASE + '/' + projectId + '/convert', {}).catch((err: any) => {
                if (isCreditsExhausted(err?.status, err?.data?.code)) {
                    // The credit gate (#74) refused /convert AFTER the blobs were
                    // written — remove the half-created project so the analyze list
                    // doesn't accumulate orphans stuck in 'uploaded' (best-effort:
                    // a failed cleanup still surfaces the credits message).
                    return this.deleteProject(projectId)
                        .catch(() => undefined)
                        .then(() => this.$q.reject(CREDITS_EXHAUSTED_MESSAGE));
                }
                return this.$q.reject(err);
            });
        }).then(() => {
            const project: AnalysisProject = {
                id: projectId,
                fileName: file.name,
                fileType: ext.replace('.', ''),
                fileSizeBytes: file.size,
                createdUtc: metadata.uploadedUtc,
                lastModifiedUtc: metadata.uploadedUtc,
                projectContext: '',
                status: 'uploaded',
                errorMessage: null,
                tasks: [],
                summary: null,
                reports: [],
            };
            return project;
        });
    }

    deleteProject(id: string): ng.IPromise<void> {
        return this.$http.delete(API_BASE + '/' + id).then(() => undefined);
    }

    /**
     * The 402 mapping lives here — at the seam that owns the /convert URL — so every
     * caller shows the same credits message.
     */
    retryConversion(id: string): ng.IPromise<void> {
        return this.$http.post(API_BASE + '/' + id + '/convert', {}).then(() => undefined, (err: any) => {
            return this.$q.reject(
                isCreditsExhausted(err?.status, err?.data?.code) ? CREDITS_EXHAUSTED_MESSAGE : err);
        });
    }

    private loadSavedReports(project: AnalysisProject): ng.IPromise<AnalysisProject> {
        const sp = this.storageFactory.getProvider('analysis-projects/' + project.id);

        return sp.list('reports/').then(files => {
            const htmlFiles = files.filter(f => f.path.endsWith('.html'));
            if (htmlFiles.length === 0) return project;

            const loadPromises = htmlFiles.map(f => {
                return sp.read(f.path).then(buffer => {
                    const html = new TextDecoder().decode(buffer);
                    const reportId = f.path.replace('reports/', '').replace('.html', '');
                    const report: Report = {
                        id: reportId,
                        title: 'Risk Report',
                        sections: [{ heading: 'Risk Report', bodyHtml: html }],
                        isComplete: true,
                        generatedUtc: f.lastModified || null,
                    };
                    return report;
                }).catch(() => null as Report | null);
            });

            return this.$q.all(loadPromises).then((reports: (Report | null)[]) => {
                const loaded = reports.filter(r => r !== null) as Report[];
                if (loaded.length > 0) {
                    project = { ...project, reports: loaded };
                }
                return project;
            });
        }).catch(() => project);
    }

    private mapServerProject(data: any): AnalysisProject {
        const summary = data.summary ? {
            projectName: data.summary.projectName || '',
            activityCount: data.summary.activityCount || 0,
            resourceCount: data.summary.resourceCount || 0,
            startDate: data.summary.startDate || '',
            endDate: data.summary.finishDate || data.summary.endDate || '',
            totalDurationDays: data.summary.totalDurationDays || 0,
            criticalPathLength: data.summary.criticalPathLength || 0,
            milestoneCount: data.summary.milestoneCount || 0,
        } : null;

        return {
            id: data.id,
            fileName: data.originalFileName || data.fileName || '',
            fileType: data.fileType || '',
            fileSizeBytes: data.fileSizeBytes || 0,
            createdUtc: data.uploadedUtc || data.createdUtc || '',
            lastModifiedUtc: data.convertedUtc || data.uploadedUtc || data.createdUtc || '',
            projectContext: data.projectContext || '',
            status: data.status || 'uploaded',
            errorMessage: data.error || data.errorMessage || null,
            tasks: [],
            summary,
            reports: [],
        };
    }

    private enrichProject(project: AnalysisProject): AnalysisProject {
        const tasks = this.processingService.getTaskStatus(project.id);
        const summary = this.processingService.getProjectSummary(project.id);

        const allReports: any[] = [];
        const processingState = this.processingService as any;
        if (processingState.states) {
            const state = processingState.states.get(project.id);
            if (state && state.reports) {
                allReports.push(...state.reports);
            }
        }

        return {
            ...project,
            tasks: tasks.length > 0 ? tasks : project.tasks,
            summary: summary || project.summary,
            reports: allReports.length > 0 ? allReports : project.reports,
        };
    }

    private extractExtension(fileName: string): string {
        const dot = fileName.lastIndexOf('.');
        return dot >= 0 ? fileName.substring(dot).toLowerCase() : '';
    }
}

gpHome.service(AnalysisProjectService.serviceName, AnalysisProjectService);
